import {
  Button,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
} from '@/components/ui'
import { Check, Eye, EyeOff } from 'lucide-react'
import { useState, type ReactNode } from 'react'

export interface ClaudeApiKeySectionProps {
  title: string
  description: string
  hasKey: boolean
  isLoading: boolean
  isSaving: boolean
  isDeleting: boolean
  onSave: (apiKey: string) => Promise<void>
  onDelete: () => Promise<void>
  onCancel?: () => void
  fallbackInfo?: ReactNode
  testIdPrefix?: string
  credentialNoun?: string
  credentialNounLabel?: string
  helpContent?: ReactNode
}

const DEFAULT_HELP_CONTENT = (
  <>
    Get your API key from{' '}
    <a
      href="https://console.anthropic.com"
      target="_blank"
      rel="noopener noreferrer"
      className="underline hover:text-foreground"
    >
      console.anthropic.com
    </a>
  </>
)

export function ClaudeApiKeySection({
  title,
  description,
  hasKey,
  isLoading,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
  onCancel,
  fallbackInfo,
  testIdPrefix = 'claude-api-key',
  credentialNoun = 'API key',
  credentialNounLabel = 'API Key',
  helpContent,
}: ClaudeApiKeySectionProps) {
  const [apiKey, setApiKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [keyError, setKeyError] = useState<string | null>(null)

  const inputId = `${testIdPrefix}-input`
  const resolvedHelpContent = helpContent === undefined ? DEFAULT_HELP_CONTENT : helpContent

  const handleSave = async () => {
    setKeyError(null)

    if (!apiKey.trim()) {
      setKeyError(`${credentialNounLabel} is required.`)
      return
    }

    if (!apiKey.startsWith('sk-ant-')) {
      setKeyError(`${credentialNounLabel} must start with 'sk-ant-'.`)
      return
    }

    try {
      await onSave(apiKey)
      setApiKey('')
      setShowKey(false)
    } catch {
      setKeyError(`Failed to save ${credentialNoun}. Please try again.`)
    }
  }

  const handleDelete = async () => {
    try {
      await onDelete()
    } catch {
      setKeyError(`Failed to remove ${credentialNoun}. Please try again.`)
    }
  }

  return (
    <section aria-label={title} data-testid={`${testIdPrefix}-section`}>
      <h2 className="text-xl font-bold mb-2 my-8">{title}</h2>
      <hr className="my-2" />
      <p className="mb-6 text-sm text-muted-foreground">{description}</p>

      {fallbackInfo && (
        <div className="mb-4" data-testid={`${testIdPrefix}-fallback-info`}>
          {fallbackInfo}
        </div>
      )}

      {isLoading ? (
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          Loading...
        </p>
      ) : (
        <>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={inputId}>{credentialNounLabel}</FieldLabel>
              <div className="relative">
                {hasKey ? (
                  <Input
                    id={inputId}
                    type="text"
                    value="••••••••••••••••••••••••"
                    disabled
                    className="text-muted-foreground"
                    data-testid={`${testIdPrefix}-input`}
                  />
                ) : (
                  <>
                    <Input
                      id={inputId}
                      type={showKey ? 'text' : 'password'}
                      placeholder="sk-ant-..."
                      value={apiKey}
                      onChange={e => {
                        setApiKey(e.target.value)
                        setKeyError(null)
                      }}
                      className="pr-10"
                      data-testid={`${testIdPrefix}-input`}
                      autoComplete="new-password"
                    />
                    {apiKey && (
                      <button
                        type="button"
                        onClick={() => setShowKey(v => !v)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                        aria-label={showKey ? `Hide ${credentialNoun}` : `Show ${credentialNoun}`}
                      >
                        {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    )}
                  </>
                )}
              </div>
              {keyError && (
                <p role="alert" className="text-sm text-destructive" data-testid={`${testIdPrefix}-error`}>
                  {keyError}
                </p>
              )}
              {resolvedHelpContent && (
                <p className="text-sm text-muted-foreground">{resolvedHelpContent}</p>
              )}
            </Field>
          </FieldGroup>

          <div className="mt-6 flex justify-end gap-3">
            {onCancel && (
              <Button
                variant="ghost"
                onClick={onCancel}
                data-testid={`${testIdPrefix}-cancel-button`}
              >
                Cancel
              </Button>
            )}
            {!hasKey && (
              <Button
                onClick={handleSave}
                disabled={isSaving}
                data-testid={`${testIdPrefix}-save-button`}
              >
                <Check />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
            {hasKey && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={isDeleting}
                data-testid={`${testIdPrefix}-delete-button`}
              >
                {isDeleting ? 'Removing...' : `Remove ${credentialNoun}`}
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  )
}
